/**
 * injectDesignData - Allows external code to inject design data into the render route
 *
 * This module provides a window API for injecting design data without fetching
 * from the proxy API. Used when navigating to the `/render/design` route
 * (no designId/designDataId params).
 *
 * The caller passes a flat designData object. RenderContainer builds a minimal
 * design entity internally from the `designId`, `width`, and `height` fields.
 *
 * Fonts are loaded automatically from the public fonts CSS based on `fontFamily`
 * values found in elements. The caller can also pass an explicit `fonts` array
 * to override this (e.g. for custom/non-public fonts).
 *
 * Usage:
 * ```javascript
 * // Navigate to /render/design
 * // Then call from console or external script:
 * window.injectDesign({
 *   id: "design-data-uuid",
 *   designId: "design-uuid",
 *   data: [{ width: 1080, height: 1920, elements: [
 *     { type: "textbox", fontFamily: "Roboto-Regular", ... }
 *   ] }],
 *   bleed: 0,
 *   documentRestrictions: [],
 *   ordered: false
 * })
 * ```
 *
 * Prerequisites:
 * - RenderContainer must be mounted via the `/render/design` route
 */

import { fetchFonts } from "lib/fonts";

const PUBLIC_FONTS_CSS_URL =
  (process.env.REACT_APP_FONT_HOST_URL ?? "") +
  (process.env.REACT_APP_PUBLIC_FONTS_PATH ?? "");

interface InjectElement {
  fontFamily?: string;
  [key: string]: unknown;
}

interface InjectDesignData {
  /** UUID of this design data version */
  id: string;
  /** UUID of the parent design */
  designId: string;
  /** Array of page objects. `width` and `height` are read from `data[0]`. */
  data: Array<{ width: number; height: number; elements: InjectElement[] }>;
  /** Bleed size in pixels */
  bleed: number;
  /** List of document-level restrictions */
  documentRestrictions: string[];
  /** Whether pages are ordered */
  ordered: boolean;
  /** Optional font definitions — overrides automatic public font extraction */
  fonts?: Array<{ name: string; stylesheetUrl: string }>;
}

type InjectHandler = (designData: InjectDesignData) => void;

let injectHandler: InjectHandler | null = null;

/**
 * Collects unique fontFamily names from elements across all pages.
 */
const extractFontNames = (data: InjectDesignData["data"]): string[] => {
  const names = new Set<string>();
  for (const page of data) {
    for (const element of page.elements) {
      if (element.fontFamily) {
        names.add(element.fontFamily);
      }
    }
  }
  return Array.from(names);
};

/**
 * Fetches the public fonts CSS, filters to only @font-face rules matching
 * the given font names, and injects them into the document head.
 */
const injectPublicFontsCss = async (fontNames: string[]): Promise<void> => {
  if (fontNames.length === 0 || !PUBLIC_FONTS_CSS_URL) return;

  const response = await fetch(PUBLIC_FONTS_CSS_URL);
  const css = await response.text();

  const blocks = css.split(/(?=@font-face)/);
  const filtered = blocks.filter(block =>
    fontNames.some(name => block.includes(`'${name}'`))
  );

  if (filtered.length === 0) return;

  const style = document.createElement("style");
  style.textContent = filtered.join("\n");
  document.head.appendChild(style);

  await document.fonts.ready;
};

/**
 * Called by external code (Puppeteer/browser console) to inject design data.
 * Loads fonts before delegating to the handler registered by RenderContainer.
 *
 * Font resolution order:
 * 1. Explicit `fonts` array if provided (for custom/non-public fonts)
 * 2. Automatic extraction from elements — fetches public-fonts.css and injects
 *    only the @font-face rules matching fontFamily names in the design data.
 */
export const injectDesignData = async (
  designData: InjectDesignData
): Promise<void> => {
  if (!injectHandler) {
    console.warn(
      "injectDesignData: No handler registered. Is RenderContainer mounted on /render/design?"
    );
    return;
  }

  if (designData.fonts && designData.fonts.length > 0) {
    await fetchFonts(
      designData.fonts.map(f => f.stylesheetUrl),
      designData.fonts.map(f => f.name)
    );
  } else {
    const fontNames = extractFontNames(designData.data);
    await injectPublicFontsCss(fontNames);
  }

  injectHandler(designData);
};

/**
 * Register a handler to receive injected design data.
 * Called by RenderContainer when injectDesign=true.
 */
export const setInjectHandler = (handler: InjectHandler): void => {
  injectHandler = handler;
};

/**
 * Unregister the inject handler. Called on component unmount.
 */
export const clearInjectHandler = (): void => {
  injectHandler = null;
};

declare global {
  interface Window {
    /** Injects design data into RenderContainer, bypassing the proxy API. */
    injectDesign?: typeof injectDesignData;
  }
}

/**
 * Registers window.injectDesign for access by external code.
 */
export const registerInjectDesignData = (): void => {
  window.injectDesign = injectDesignData;
};
